<template>
  <div class="container driver-container">
    <base-nav title="Водители">
      <base-primary-button
        icon="addSquare"
        @click="goToDriverCreate"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Driver"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="!dataIsLoading && drivers && drivers.length > 0"
      :pagination-meta="pagination"
    />

    <!-- Переключение вида -->
    <TableToolbar
      v-if="!dataIsLoading && drivers.length > 0"
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? dropdownItems : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        drivers &&
        drivers.length > 0 &&
        currentView === 'table'
      "
      class="driver-center"
    >
      <TableComponent
        :data="drivers"
        :columns="tableColumns"
        :ready="!dataIsLoading && tableColumns.length > 0"
        selectable
        @selection-change="onSelectionChange"
        @row-middle-click="
          openRouteInNewTab({
            name: 'DriverDetails',
            params: { id: $event.id },
          })
        "
        @row-click="goToDriverDetails"
      />
    </div>

    <!-- Карточный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        drivers &&
        drivers.length > 0 &&
        currentView === 'cards'
      "
      class="driver-block"
    >
      <ul class="driver-list">
        <li
          class="card driver-item"
          v-for="(driver, index) in drivers"
          :key="index"
          @click="goToDriverDetails(driver.id)"
        >
          <h3
            class="item-title"
            v-if="
              driver.last_name ||
              driver.first_name ||
              driver.middle_name
            "
          >
            {{ driver.last_name }}
            {{ driver.first_name }}
            {{ driver.middle_name }}
          </h3>
          <div class="item-center" v-if="driver.phone">
            <span class="value" v-if="driver.phone">
              Телефон: {{ driver.phone }}
            </span>
          </div>

          <div
            class="item-bottom"
            v-if="driver.created_at || driver.updated_at"
          >
            <MetaTime
              :createdAt="driver.created_at"
              :updatedAt="driver.updated_at"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Пагинация -->
    <pagination-component
      v-if="
        pagination.pages > 1 &&
        !dataIsLoading &&
        tableColumns.length > 0
      "
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="currentRouteQuery"
    />

    <div class="no-data" v-if="!dataIsLoading && drivers.length == 0">
      Список водителей пуст
    </div>

    <!-- Модальное окно настроек -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Driver"
        record-type="Driver"
        table-name="drivers"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>
  </div>
</template>

<script>
import driversApi from "@/services/api/driversApi";
import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";

import { getCompanyId } from "@/utils/userUtils";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";

import FilterBlock from "@/components/Filter/FilterBlock.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import MetaTime from "@/components/Meta/MetaTime.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableComponent from "@/components/TableComponent.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";

import rawSearchFields from "@/config/filters/driver/drivers.json";

export default {
  name: "DriversPage",
  components: {
    FilterBlock,
    PaginationComponent,
    MetaTime,
    MetaResultCount,
    TableComponent,
    TableSettingsModal,
    TableToolbar,
  },
  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: true,
      currentView: "table",
      showSettingsModal: false,

      drivers: [],
      tableColumns: [],
      selectedDrivers: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },

      dropdownItems: [
        {
          id: "enable",
          label: "Показать",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Скрыть",
          isDanger: true,
          action: this.disableSelected,
        },
      ],

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.getDriversRequest();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedDrivers = [];
      }
    },
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },

    searchFields() {
      return rawSearchFields;
    },
  },
  methods: {
    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Driver",
            "drivers"
          );

        this.tableColumns = columns;
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы водителей",
          error
        );
        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    async getDriversRequest() {
      const page = getCurrentPageFromUrl(this.$route);

      try {
        this.dataIsLoading = true;

        const params = {
          page: page,
          ...filtersToApiParams(this.appliedFilters),
          include_attributes: true,
        };

        const response = await driversApi.index(
          this.companyId,
          params
        );

        this.drivers =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            response.data.result,
            this.tableColumns
          );

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };

        this.selectedDrivers = [];
      } catch (error) {
        console.error("Ошибка при загрузке водителей", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedDrivers = [];
    },

    goToDriverCreate() {
      this.$router.push({ name: "DriverCreate" });
    },

    goToDriverDetails(driver) {
      const driverId = driver.id || driver;
      this.$router.push({
        name: "DriverDetails",
        params: { id: driverId },
      });
    },

    onSelectionChange(selected) {
      this.selectedDrivers = selected;
    },

    async enableSelected() {
      if (this.selectedDrivers.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного водителя"
        );
        return;
      }

      try {
        const enablePromises = this.selectedDrivers.map((driver) =>
          driversApi.enable(this.companyId, driver.id)
        );

        await Promise.all(enablePromises);

        this.notificationStore.showSuccess(
          `Успешно включено ${this.selectedDrivers.length} водителей`
        );

        await this.getDriversRequest();
      } catch (error) {
        console.error("Ошибка при включении водителей:", error);
      }
    },

    async disableSelected() {
      if (this.selectedDrivers.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы одного водителя"
        );
        return;
      }

      try {
        const disablePromises = this.selectedDrivers.map((driver) =>
          driversApi.disable(this.companyId, driver.id)
        );

        await Promise.all(disablePromises);

        this.notificationStore.showSuccess(
          `Успешно отключено ${this.selectedDrivers.length} водителей`
        );

        await this.getDriversRequest();
      } catch (error) {
        console.error("Ошибка при отключении водителей:", error);
      }
    },

    openRouteInNewTab,
  },
  async created() {
    this.updateFiltersFromUrl();
    await this.loadTableConfig();
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getDriversRequest();
  },
};
</script>

<style scoped>
.driver-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.driver-item {
  cursor: pointer;
}

.driver-item:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.item-title {
  max-width: 60%;
}

.item-center {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: var(--base-in-indent);
}

.item-bottom {
  display: flex;
  flex-direction: column;
  margin-top: var(--base-in-indent);
}

</style>
